import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { PriorityBadge, StatusBadge, ProgressBar, Avatar } from "./Badges";
import { PRIORITIES, fmtDate, isOverdue, PRIORITY_ORDER, STATUS_ORDER } from "../lib/constants";
import { ArrowUpDown, Trash2, AlertTriangle, Pencil, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";

const COLS = [
  { key: "dev_id", label: "ID" },
  { key: "title", label: "Desarrollo" },
  { key: "sistema", label: "Sistema / Módulo" },
  { key: "responsable", label: "Responsable" },
  { key: "priority", label: "Prioridad" },
  { key: "status", label: "Estado" },
  { key: "fecha_compromiso", label: "Fecha compromiso" },
  { key: "avance", label: "% Avance" },
];

export default function DevTable({ devs, onRowClick, onDelete, onNotify, isPM, canWrite, selectable, selectedIds, onToggleSelect, onToggleSelectAll }) {
  const [sortKey, setSortKey] = useState(null);
  const [asc, setAsc] = useState(true);

  const sorted = React.useMemo(() => {
    if (!sortKey) return devs;
    const arr = [...devs];
    arr.sort((a, b) => {
      let va, vb;
      if (sortKey === "priority") { va = PRIORITY_ORDER.indexOf(a.priority); vb = PRIORITY_ORDER.indexOf(b.priority); }
      else if (sortKey === "status") { va = STATUS_ORDER.indexOf(a.status); vb = STATUS_ORDER.indexOf(b.status); }
      else if (sortKey === "responsable") { va = a.responsables?.[0]?.name || ""; vb = b.responsables?.[0]?.name || ""; }
      else if (sortKey === "fecha_compromiso") { va = a.fecha_compromiso || ""; vb = b.fecha_compromiso || ""; }
      else { va = a[sortKey]; vb = b[sortKey]; }
      if (va < vb) return asc ? -1 : 1;
      if (va > vb) return asc ? 1 : -1;
      return 0;
    });
    return arr;
  }, [devs, sortKey, asc]);

  const toggleSort = (key) => {
    if (sortKey === key) setAsc(!asc);
    else { setSortKey(key); setAsc(true); }
  };

  return (
    <div className="border border-border bg-white overflow-x-auto" data-testid="dev-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50 hover:bg-slate-50">
            {selectable && (
              <TableHead className="w-8">
                <Checkbox
                  checked={sorted.length > 0 && sorted.every((d) => selectedIds?.has(d.id))}
                  onCheckedChange={() => onToggleSelectAll?.(sorted)}
                  data-testid="dev-select-all"
                />
              </TableHead>
            )}
            {COLS.map((c) => (
              <TableHead key={c.key}>
                <button onClick={() => toggleSort(c.key)} data-testid={`sort-${c.key}`}
                  className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 transition-colors duration-200">
                  {c.label} <ArrowUpDown className="h-3 w-3 opacity-50" />
                </button>
              </TableHead>
            ))}
            <TableHead className="text-right text-[11px] font-bold uppercase tracking-wide text-slate-600">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((d) => (
            <TableRow key={d.id} onClick={() => onRowClick(d)} data-testid={`table-row-${d.dev_id}`}
              className="cursor-pointer hover:bg-slate-50 transition-colors duration-200">
              {selectable && (
                <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selectedIds?.has(d.id)} onCheckedChange={() => onToggleSelect?.(d.id)} data-testid={`dev-select-${d.dev_id}`} />
                </TableCell>
              )}
              <TableCell className="font-mono text-xs text-slate-500">{d.dev_id}</TableCell>
              <TableCell className="font-medium max-w-[260px]">
                <div className="flex items-center gap-1.5">
                  {isOverdue(d.fecha_compromiso, d.status) && <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                  <span className="truncate">{d.title}</span>
                </div>
              </TableCell>
              <TableCell>
                {d.sistema || d.modulo || d.submodulo ? (
                  <div className="flex items-center gap-1 text-xs text-slate-600 flex-wrap">
                    {d.sistema && <span className="font-semibold text-slate-800">{d.sistema}</span>}
                    {d.modulo && <span className="text-slate-400">›</span>}
                    {d.modulo && <span>{d.modulo}</span>}
                    {d.submodulo && <span className="text-slate-400">›</span>}
                    {d.submodulo && <span className="text-slate-500">{d.submodulo}</span>}
                  </div>
                ) : <span className="text-slate-300 text-sm">—</span>}
              </TableCell>
              <TableCell>
                {(d.responsables && d.responsables.length > 0) ? (
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {d.responsables.slice(0, 3).map((r) => (
                        <div key={r.id} title={r.name} className="ring-2 ring-white rounded-full"><Avatar user={r} size={22} /></div>
                      ))}
                    </div>
                    <span className="text-sm truncate max-w-[120px]">
                      {d.responsables.length === 1 ? d.responsables[0].name : `${d.responsables.length} responsables`}
                    </span>
                  </div>
                ) : <span className="text-slate-300 text-sm">Sin asignar</span>}
              </TableCell>
              <TableCell><PriorityBadge priority={d.priority} /></TableCell>
              <TableCell><StatusBadge status={d.status} /></TableCell>
              <TableCell className={`font-mono text-xs ${isOverdue(d.fecha_compromiso, d.status) ? "text-red-600 font-bold" : "text-slate-600"}`}>
                {fmtDate(d.fecha_compromiso)}
              </TableCell>
              <TableCell className="w-32">
                <div className="flex items-center gap-2">
                  <ProgressBar value={d.avance} color={PRIORITIES[d.priority]?.dot} height={4} />
                  <span className="font-mono text-xs text-slate-500 w-8">{d.avance}%</span>
                </div>
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1">
                  {canWrite && (
                    <Button variant="outline" size="sm" data-testid={`edit-${d.dev_id}`}
                      className="h-8 rounded-sm border-slate-300 text-slate-700 hover:border-slate-900 hover:text-slate-900" onClick={() => onRowClick(d)}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                    </Button>
                  )}
                  {canWrite && onNotify && (
                    <Button variant="outline" size="sm" data-testid={`notify-${d.dev_id}`} title="Enviar detalles por correo"
                      className="h-8 w-8 p-0 rounded-sm border-slate-300 text-slate-600 hover:border-slate-900 hover:text-slate-900" onClick={() => onNotify(d)}>
                      <Mail className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {isPM && (
                    <Button variant="outline" size="sm" data-testid={`delete-${d.dev_id}`}
                      className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:border-red-400" onClick={() => onDelete(d)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {!canWrite && !isPM && <span className="text-slate-300 text-xs">—</span>}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {sorted.length === 0 && (
            <TableRow><TableCell colSpan={selectable ? 10 : 9} className="text-center text-slate-400 py-10">No hay desarrollos.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
